import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { getCsed } from "@/features/transcripts/api";
import type { CsedResult } from "@/features/transcripts/lib/csed";
import { ApiError } from "@/lib/api";

/**
 * Per-row CSED breakdown, fetched lazily the first time the popover opens
 * (the list endpoint carries only the denormalized `estimatedCsed`, not the
 * full due-date/tolling breakdown).
 */
export function CsedPopover({ clientId, recordId }: { clientId: string; recordId: string }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<CsedResult | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open || result) return;
    let active = true;
    getCsed(clientId, recordId)
      .then((r) => active && setResult(r))
      .catch((e: unknown) => active && setError(e instanceof ApiError ? e.message : "Failed to load."));
    return () => {
      active = false;
    };
  }, [open, result, clientId, recordId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="CSED breakdown"
          onClick={(e) => e.stopPropagation()}
        >
          <CalendarClock className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" onClick={(e) => e.stopPropagation()}>
        <h4 className="text-foreground mb-2 text-sm font-semibold">CSED Breakdown</h4>
        {error ? (
          <p className="text-destructive text-sm">{error}</p>
        ) : !result ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : result.dueDates.length === 0 ? (
          <p className="text-muted-foreground text-sm">No qualifying assessment — no CSED computed.</p>
        ) : (
          <div className="flex flex-col gap-2">
            <ul className="flex flex-col gap-1 text-sm">
              {result.dueDates.map((dd) => (
                <li key={`${dd.triggerCode}-${dd.triggerDate}`} className="flex justify-between">
                  <span className="text-muted-foreground">
                    TC {dd.triggerCode} · {dd.triggerDate}
                  </span>
                  <span className="text-foreground font-semibold">{dd.dueDate}</span>
                </li>
              ))}
            </ul>
            {result.tollingActive && (
              <Badge variant="outline" className="border-warning/20 bg-warning/10 text-warning w-fit text-[10px] font-bold uppercase">
                Tolling active
              </Badge>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
